import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { RefreshCw, LogOut, ChevronDown, AlertTriangle, Settings, Clock, Users, X, Check, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { Navigation } from './Navigation';

// API is now imported from utils

// ============== SPEEDOMETER COMPONENT ==============
const Speedometer = ({ value, label, size = 96 }) => {
  // KPT color thresholds: Green ≥ 0.49, Yellow 0.45-0.48, Red < 0.45
  const getColor = (val) => {
    if (val >= 0.49) return '#2E7D32'; // Muted green
    if (val >= 0.45) return '#C77700'; // Muted orange
    return '#B3261E'; // Muted red
  };

  const color = getColor(value);
  const maxValue = 0.7;
  const percentage = Math.min((value / maxValue) * 100, 100);
  const angle = (percentage / 100) * 180;
  
  const radius = 45;
  const strokeWidth = 8;
  const center = 50;
  
  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 180) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad)
    };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox="0 0 100 65">
        {/* Background arc */}
        <path
          d={describeArc(center, 55, radius, 0, 180)}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Green zone (≥0.49) - right side */}
        <path
          d={describeArc(center, 55, radius, 126, 180)}
          fill="none"
          stroke="#2E7D32"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Yellow zone (0.45-0.48) */}
        <path
          d={describeArc(center, 55, radius, 116, 126)}
          fill="none"
          stroke="#C77700"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Red zone (<0.45) - left side */}
        <path
          d={describeArc(center, 55, radius, 0, 116)}
          fill="none"
          stroke="#B3261E"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Value arc */}
        {angle > 0 && (
          <path
            d={describeArc(center, 55, radius, 0, Math.min(angle, 180))}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        {/* Needle */}
        <line
          x1={center}
          y1="55"
          x2={center + (radius - 10) * Math.cos((angle - 180) * Math.PI / 180)}
          y2={55 + (radius - 10) * Math.sin((angle - 180) * Math.PI / 180)}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Center dot */}
        <circle cx={center} cy="55" r="4" fill={color} />
      </svg>
      <p className="text-2xl font-bold mt-2" style={{ color }}>{value.toFixed(2)}</p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</p>
    </div>
  );
};

// ============== HOURS GAUGE COMPONENT ==============
const HoursGauge = ({ value, label, size = 96 }) => {
  // Hours color based on capacity - adjust thresholds as needed
  // Assuming ~500 hours per week is target capacity
  const getColor = (val) => {
    if (val >= 500) return '#2E7D32'; // Green - good capacity
    if (val >= 400) return '#C77700'; // Yellow - moderate
    return '#B3261E'; // Red - low capacity
  };

  const color = getColor(value);
  const maxValue = 700; // Max display value for gauge
  const percentage = Math.min((value / maxValue) * 100, 100);
  const angle = (percentage / 100) * 180;
  
  const radius = 45;
  const strokeWidth = 8;
  const center = 50;
  
  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 180) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad)
    };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox="0 0 100 65">
        {/* Background arc */}
        <path
          d={describeArc(center, 55, radius, 0, 180)}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Green zone (≥500) - right side */}
        <path
          d={describeArc(center, 55, radius, 129, 180)}
          fill="none"
          stroke="#2E7D32"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Yellow zone (400-500) */}
        <path
          d={describeArc(center, 55, radius, 103, 129)}
          fill="none"
          stroke="#C77700"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Red zone (<400) - left side */}
        <path
          d={describeArc(center, 55, radius, 0, 103)}
          fill="none"
          stroke="#B3261E"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity="0.25"
        />
        {/* Value arc */}
        {angle > 0 && (
          <path
            d={describeArc(center, 55, radius, 0, Math.min(angle, 180))}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        {/* Needle */}
        <line
          x1={center}
          y1="55"
          x2={center + (radius - 10) * Math.cos((angle - 180) * Math.PI / 180)}
          y2={55 + (radius - 10) * Math.sin((angle - 180) * Math.PI / 180)}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Center dot */}
        <circle cx={center} cy="55" r="4" fill={color} />
      </svg>
      <p className="text-2xl font-bold mt-2" style={{ color }}>{Math.round(value)}</p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</p>
    </div>
  );
};

// ============== PROGRESS BAR COMPONENT ==============
const ProgressBar = ({ value, max, label, showPercentage = true, color = '#1E6F7A', budgetLine = null, budgetLabel = null }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const budgetPercentage = budgetLine ? Math.min((budgetLine / max) * 100, 100) : null;
  
  const getStatusColor = (pct) => {
    if (pct >= 100) return '#2E7D32';
    if (pct >= 70) return '#1E6F7A';
    if (pct >= 40) return '#C77700';
    return '#B3261E';
  };
  
  const barColor = color === 'auto' ? getStatusColor(percentage) : color;
  
  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between mb-1">
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
          {showPercentage && (
            <span className="text-xs font-medium" style={{ color: barColor }}>{percentage.toFixed(0)}%</span>
          )}
        </div>
      )}
      <div className="relative">
        <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: barColor }}
          />
          {budgetPercentage !== null && (
            <div 
              className="absolute top-0 h-full w-0.5 bg-white"
              style={{ left: `${budgetPercentage}%` }}
            />
          )}
        </div>
        {budgetPercentage !== null && budgetLabel && (
          <span 
            className="absolute text-xs text-white/60 mt-1"
            style={{ left: `${budgetPercentage}%`, transform: 'translateX(-50%)' }}
          >
            {budgetLabel}
          </span>
        )}
      </div>
    </div>
  );
};

// ============== STATUS INDICATOR ==============
const StatusIndicator = ({ value, thresholds, labels }) => {
  let status, color, icon;
  
  if (value >= thresholds.good) {
    status = labels?.good || 'På mål';
    color = '#2E7D32';
    icon = <TrendingUp className="w-3 h-3" />;
  } else if (value >= thresholds.warning) {
    status = labels?.warning || 'Under mål';
    color = '#C77700';
    icon = <TrendingDown className="w-3 h-3" />;
  } else {
    status = labels?.bad || 'Kritisk';
    color = '#B3261E';
    icon = <TrendingDown className="w-3 h-3" />;
  }
  
  return (
    <div className="flex items-center gap-1.5 mt-2" style={{ color }}>
      {icon}
      <span className="text-xs font-medium">{status}</span>
    </div>
  );
};

// ============== MINI REGION CHART ==============
const MiniRegionChart = ({ data, region, color }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs w-16" style={{ color: 'rgba(255,255,255,0.6)' }}>{region}</span>
      <div className="flex-1 h-8">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              fill={color}
              fillOpacity={0.15}
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <span className="text-xs font-medium w-8" style={{ color }}>{data[data.length - 1]?.value || 0}</span>
    </div>
  );
};

// ============== SECTION CARD (Nordic Financial Style) ==============
const SectionCard = ({ title, children, className = '' }) => (
  <div className={`bg-[#1A1A1A] rounded-lg p-5 card-shadow ${className}`}>
    <h4 className="text-sm font-medium mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>{title}</h4>
    {children}
  </div>
);

// ============== MAIN DASHBOARD COMPONENT ==============
export const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [onlineCoverageData, setOnlineCoverageData] = useState(null);  // Online sales coverage
  const [orderData, setOrderData] = useState(null);  // Order data from Data_klar
  const [monthlyOrderData, setMonthlyOrderData] = useState(null);  // Monthly order summary
  const [saldoCurrentMonth, setSaldoCurrentMonth] = useState(null);  // Saldo omsætning for current month
  const [newBookingsData, setNewBookingsData] = useState(null);  // NEW: New bookings this week from Leadvalidator
  const [calendarCapacityData, setCalendarCapacityData] = useState(null);  // Calendar bookings and free slots
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  
  // Filters
  const [region, setRegion] = useState('Hele landet');
  const [excludedEmployees, setExcludedEmployees] = useState([]);
  
  // Panels
  const [showDebug, setShowDebug] = useState(false);
  const [showEmployeePanel, setShowEmployeePanel] = useState(false);

  // Initialize with default exclusions (Montør and Leder)
  useEffect(() => {
    const initializeDefaults = async () => {
      try {
        const response = await api.get('/dashboard/data', {
          params: { region: 'Hele landet', week_offset: 0, excluded_employees: '' }
        });
        
        const defaultExcluded = (response.data.employees || [])
          .filter(emp => emp.employee_type === 'Montør' || emp.employee_type === 'Leder')
          .map(emp => emp.email);
        
        setExcludedEmployees(defaultExcluded);
        setInitialized(true);
      } catch (err) {
        console.error('Init error:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        }
        setInitialized(true);
      }
    };
    
    initializeDefaults();
  }, [navigate]);

  const fetchDashboardData = useCallback(async () => {
    if (!initialized) return;
    
    try {
      const response = await api.get('/dashboard/data', {
        params: { 
          region, 
          week_offset: 0,
          excluded_employees: excludedEmployees.join(',')
        }
      });
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [region, excludedEmployees, navigate, initialized]);

  // Fetch coverage data for "Dækning denne uge" section from Data_klar
  const fetchCoverageData = useCallback(async () => {
    try {
      setCoverageLoading(true);
      // Fetch current week's coverage from Data_klar (uses column AA for dækningsbidrag)
      const response = await api.get('/orders/current-week');
      console.log('Coverage data response:', response.data);
      console.log('Coverage data summary:', response.data?.summary);
      console.log('Coverage data total_db:', response.data?.summary?.total_db);
      setCoverageData(response.data);
    } catch (err) {
      console.error('Coverage fetch error:', err);
      console.error('Coverage fetch error response:', err.response);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      // Set empty data on error so UI doesn't show "Indlæser..." forever
      setCoverageData({ summary: { total_db: 0, total_orders: 0 } });
    } finally {
      setCoverageLoading(false);
    }
  }, [navigate]);

  // NEW: Fetch order data from Data_klar + Saldo for monthly overview
  const fetchOrderData = useCallback(async () => {
    try {
      // Fetch weekly summary for charts
      const weeklyResponse = await api.get('/orders/weekly-summary', {
        params: { year: new Date().getFullYear().toString() }
      });
      setOrderData(weeklyResponse.data);
      
      // Fetch monthly summary for accumulated charts
      const monthlyResponse = await api.get('/orders/monthly-summary');
      setMonthlyOrderData(monthlyResponse.data);
      
      // Fetch saldobalance omsætning for current month (same method as Saldobalance page)
      try {
        const saldoResponse = await api.get('/saldobalance/omsaetning');
        const saldoData = saldoResponse.data?.data || [];
        // Find last month with actual data (omsaetning > 0)
        const lastWithData = [...saldoData].reverse().find(m => m.omsaetning > 0);
        if (lastWithData) {
          setSaldoCurrentMonth(lastWithData);
        }
      } catch (saldoErr) {
        console.error('Saldo fetch error:', saldoErr);
      }
    } catch (err) {
      console.error('Order data fetch error:', err);
    }
  }, []);

  // Fetch new bookings this week from Leadvalidator Export sheet
  const fetchNewBookingsData = useCallback(async () => {
    try {
      const response = await api.get('/bookings/new-this-week');
      console.log('New bookings data response:', response.data);
      console.log('New bookings price_per_booking:', response.data?.price_per_booking);
      setNewBookingsData(response.data);
    } catch (err) {
      console.error('New bookings data fetch error:', err);
      console.error('New bookings error response:', err.response);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate]);

  // Fetch calendar capacity data (bookings and free slots for this week and next week)
  const fetchCalendarCapacityData = useCallback(async () => {
    try {
      const response = await api.get('/bookings/calendar-capacity');
      console.log('Calendar capacity data response:', response.data);
      console.log('Calendar capacity this_week:', response.data?.this_week);
      console.log('Calendar capacity belaegning:', response.data?.this_week?.belaegning);
      setCalendarCapacityData(response.data);
    } catch (err) {
      console.error('Calendar capacity data fetch error:', err);
      console.error('Calendar capacity error response:', err.response);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate]);

  // Fetch online sales coverage from the Online Sales sheet
  // Fetch online sales coverage from the Online Sales sheet
  const fetchOnlineCoverageData = useCallback(async () => {
    try {
      const response = await api.get('/online-sales/coverage');
      console.log('Online coverage data response:', response.data);
      console.log('Online coverage this_week_coverage:', response.data?.this_week_coverage);
      setOnlineCoverageData(response.data);
    } catch (err) {
      console.error('Online coverage data fetch error:', err);
      console.error('Online coverage error response:', err.response);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
    fetchCoverageData();
    fetchOrderData();
    fetchNewBookingsData();
    fetchCalendarCapacityData();
    fetchOnlineCoverageData();
  }, [fetchDashboardData, fetchCoverageData, fetchOrderData, fetchNewBookingsData, fetchCalendarCapacityData, fetchOnlineCoverageData, navigate]);

  // Employee filter helpers
  const toggleEmployeeExclusion = (email) => {
    setExcludedEmployees(prev => 
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const excludeByType = (type) => {
    const emailsOfType = (dashboardData?.employees || [])
      .filter(emp => emp.employee_type === type)
      .map(emp => emp.email);
    setExcludedEmployees(prev => Array.from(new Set([...prev, ...emailsOfType])));
  };

  const includeByType = (type) => {
    const emailsOfType = (dashboardData?.employees || [])
      .filter(emp => emp.employee_type === type)
      .map(emp => emp.email);
    setExcludedEmployees(prev => prev.filter(e => !emailsOfType.includes(e)));
  };

  const isTypeFullyExcluded = (type) => {
    const emailsOfType = (dashboardData?.employees || [])
      .filter(emp => emp.employee_type === type)
      .map(emp => emp.email);
    return emailsOfType.length > 0 && emailsOfType.every(e => excludedEmployees.includes(e));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/dashboard/refresh');
      await api.post('/orders/refresh');  // NEW: Refresh order data too
      await Promise.all([fetchDashboardData(), fetchCoverageData(), fetchOrderData()]);
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  const formatLastSync = (isoString) => {
    if (!isoString || isoString === 'Never') return 'Never';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('da-DK', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
    } catch {
      return 'Unknown';
    }
  };

  // Calculate coverage this week from Data_klar (uses daekningsbidrag column AA)
  console.log('Render - coverageData:', coverageData);
  console.log('Render - coverageData?.summary:', coverageData?.summary);
  const coverageThisWeek = Number(coverageData?.summary?.total_db) || 0;
  console.log('Render - coverageThisWeek calculated:', coverageThisWeek);
  const coverageGoal = 700000; // 700.000 kr budget goal
  const coveragePossible = 775000; // Mulig dækning beregnet ud fra bookinger i kalenderen (placeholder)
  const coveragePercentage = (coverageThisWeek / coverageGoal) * 100;
  const totalOrdersThisWeek = coverageData?.summary?.total_orders || 0;

  // Online sales coverage from the Online Sales sheet (column AI in Data_klar)
  console.log('Render - onlineCoverageData:', onlineCoverageData);
  const onlineCoverageThisWeek = Number(onlineCoverageData?.this_week_coverage) || 0;
  console.log('Render - onlineCoverageThisWeek calculated:', onlineCoverageThisWeek);
  const onlineGoal = 100000;
  const onlinePercentage = (onlineCoverageThisWeek / onlineGoal) * 100;

  // Bookinger data fra calendar capacity endpoint (Data_klar_1: opmaling_count & gap_time_min_90)
  // Debug: log calendarCapacityData to see what's being received
  console.log('calendarCapacityData:', calendarCapacityData);
  const bookingsThisWeek = {
    bookinger: Number(calendarCapacityData?.this_week?.bookinger) || 0,
    kapacitet: Number(calendarCapacityData?.this_week?.kapacitet) || 1, // Avoid division by zero
    ledigeTider: Number(calendarCapacityData?.this_week?.ledige_tider) || 0,
    belaegning: Number(calendarCapacityData?.this_week?.belaegning) || 0
  };
  console.log('bookingsThisWeek:', bookingsThisWeek);
  const bookingsNextWeek = {
    bookinger: Number(calendarCapacityData?.next_week?.bookinger) || 0,
    kapacitet: Number(calendarCapacityData?.next_week?.kapacitet) || 1, // Avoid division by zero
    ledigeTider: Number(calendarCapacityData?.next_week?.ledige_tider) || 0,
    belaegning: Number(calendarCapacityData?.next_week?.belaegning) || 0
  };

  // Region chart data from weekly order data
  const regionChartData = {
    sjaelland: [{ value: 12 }, { value: 15 }, { value: 18 }, { value: 14 }, { value: 20 }],
    fyn: [{ value: 5 }, { value: 7 }, { value: 6 }, { value: 8 }, { value: 9 }],
    jylland: [{ value: 8 }, { value: 10 }, { value: 12 }, { value: 9 }, { value: 15 }]
  };

  // Monthly economy data - use Saldo_Data_Klar (same as Saldobalance page) when available
  const monthlyTotalRevenue = saldoCurrentMonth?.omsaetning || monthlyOrderData?.summary?.total_revenue || 0;
  const monthlyTotalDB = saldoCurrentMonth?.daekningsbidrag || monthlyOrderData?.summary?.total_db || 0;
  
  // Budget targets (these could come from config/API in the future)
  const monthlyRevenueTarget = 5000000;  // 5M kr budget target
  const monthlyBreakeven = 3500000;      // 3.5M kr break-even
  const monthlyDBTarget = 1500000;       // 1.5M kr DB target
  const monthlyDBBreakeven = 1000000;    // 1M kr DB break-even

  // Daily accumulated data from API (or generate from monthly data)
  const dailyOmsaetningData = (monthlyOrderData?.daily_data || []).map((day, index, arr) => {
    // Calculate days in current month for break-even line
    const daysInMonth = new Date(monthlyOrderData?.year || new Date().getFullYear(), monthlyOrderData?.month || new Date().getMonth() + 1, 0).getDate();
    const dailyBreakeven = monthlyBreakeven / daysInMonth;
    
    return {
      dag: day.dag,
      omsaetning: day.akkumuleret_omsaetning,
      breakeven: dailyBreakeven * parseInt(day.dag)
    };
  });

  // Fill remaining days of month with null omsaetning but continue breakeven line
  const today = new Date().getDate();
  const currentMonth = monthlyOrderData?.month || new Date().getMonth() + 1;
  const currentYear = monthlyOrderData?.year || new Date().getFullYear();
  const daysInCurrentMonth = new Date(currentYear, currentMonth, 0).getDate();
  
  // Add future days with null values
  for (let i = dailyOmsaetningData.length + 1; i <= daysInCurrentMonth; i++) {
    const dailyBreakeven = monthlyBreakeven / daysInCurrentMonth;
    dailyOmsaetningData.push({
      dag: String(i),
      omsaetning: null,
      breakeven: dailyBreakeven * i
    });
  }

  // Daily DB data from API
  const dailyDBData = (monthlyOrderData?.daily_data || []).map((day) => {
    const daysInMonth = new Date(monthlyOrderData?.year || new Date().getFullYear(), monthlyOrderData?.month || new Date().getMonth() + 1, 0).getDate();
    const dailyDBBreakeven = monthlyDBBreakeven / daysInMonth;
    
    return {
      dag: day.dag,
      db: day.akkumuleret_db,
      breakeven: dailyDBBreakeven * parseInt(day.dag)
    };
  });

  // Add future days with null values for DB
  for (let i = dailyDBData.length + 1; i <= daysInCurrentMonth; i++) {
    const dailyDBBreakeven = monthlyDBBreakeven / daysInCurrentMonth;
    dailyDBData.push({
      dag: String(i),
      db: null,
      breakeven: dailyDBBreakeven * i
    });
  }

  // Data for nye bookinger akkumuleret per dag - from Leadvalidator Export sheet
  // Daily target now comes from API (Konsulent_Data kolonne H)
  const nyeBookingerData = (newBookingsData?.daily_data || [
    { dag: 'Man', akkumuleret: null, maal: 36 },
    { dag: 'Tir', akkumuleret: null, maal: 72 },
    { dag: 'Ons', akkumuleret: null, maal: 108 },
    { dag: 'Tor', akkumuleret: null, maal: 144 },
    { dag: 'Fre', akkumuleret: null, maal: 180 },
    { dag: 'Lør', akkumuleret: null, maal: 216 },
    { dag: 'Søn', akkumuleret: null, maal: 252 }
  ]).map((day) => ({
    dag: day.dag,
    akkumuleret: day.akkumuleret,
    maal: day.maal  // Use target from API
  }));
  
  // Weekly target from API (sum of all consultants' weekly needs)
  const weeklyBookingTarget = Number(newBookingsData?.weekly_target) || 252;
  
  // Price per booking from API (total spend / total bookings)
  const pricePerBooking = Number(newBookingsData?.price_per_booking) || 0;
  
  // Find today's bookings from the daily data
  const todaysDayName = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'][new Date().getDay()];
  const todaysData = newBookingsData?.daily_data?.find(d => d.dag === todaysDayName);
  const todaysBookings = todaysData?.nye_bookinger ?? 0;

  // Besøg og konvertering (keeping as placeholder - needs analytics integration)
  const besogKonverteringData = [
    { uge: 'Uge 5', besog: 12400, konvertering: 2.1 },
    { uge: 'Uge 6', besog: 13200, konvertering: 2.4 },
    { uge: 'Uge 7', besog: 11800, konvertering: 1.9 },
    { uge: 'Uge 8', besog: 14500, konvertering: 2.6 },
    { uge: 'Uge 9', besog: 15200, konvertering: 2.8 },
    { uge: 'Uge 10', besog: 13900, konvertering: 2.3 },
    { uge: 'Uge 11', besog: 16100, konvertering: 3.1 },
    { uge: 'Uge 12', besog: 14800, konvertering: 2.7 }
  ];

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Navigation />
              <h1 className="text-xl font-semibold text-white">
                Luxsun Nordic - Ledelse
              </h1>
              <div className="hidden md:flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                <Clock className="w-4 h-4" />
                <span className="text-xs">Sync: {formatLastSync(dashboardData?.last_sync)}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={handleRefresh} disabled={refreshing} className="p-2 rounded-lg bg-[#1A1A1A] text-white/60 hover:text-white hover:bg-[#252525] transition-colors disabled:opacity-50" data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error/Warnings */}
      {error && (
        <div className="max-w-[1920px] mx-auto px-6 mt-4">
          <div className="warning-banner flex items-center gap-3"><AlertTriangle className="w-4 h-4 flex-shrink-0" /><span>{error}</span></div>
        </div>
      )}

      {/* Main 3-Column Layout */}
      <main className="max-w-[1920px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* ========== LEFT COLUMN: ØKONOMI ========== */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white pb-2 border-b border-white/10">
              Økonomi
            </h2>

            <SectionCard title="Månedsoverblik">
              <div className="space-y-4">
                {/* Omsætning MTD */}
                <div>
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Omsætning MTD</span>
                    <span className="text-lg font-bold text-white">
                      {monthlyTotalRevenue > 0 ? `${(monthlyTotalRevenue / 1000000).toFixed(2)}M kr` : '0 kr'}
                    </span>
                  </div>
                  <div className="relative h-4 bg-white/10 rounded-full overflow-hidden">
                    {/* Break-even marker */}
                    <div className="absolute top-0 bottom-0 w-0.5 bg-white/50 z-10" style={{ left: `${(monthlyBreakeven / monthlyRevenueTarget) * 100}%` }}></div>
                    {/* Progress bar with gradient zones */}
                    {(() => {
                      const revenuePercent = Math.min((monthlyTotalRevenue / monthlyRevenueTarget) * 100, 100);
                      const breakevenPercent = (monthlyBreakeven / monthlyRevenueTarget) * 100;
                      let gradient;
                      if (revenuePercent <= breakevenPercent) {
                        gradient = '#B3261E'; // All red
                      } else if (revenuePercent < 100) {
                        gradient = `linear-gradient(to right, #B3261E 0%, #B3261E ${breakevenPercent}%, #C77700 ${breakevenPercent}%, #C77700 100%)`;
                      } else {
                        gradient = `linear-gradient(to right, #B3261E 0%, #B3261E ${breakevenPercent}%, #C77700 ${breakevenPercent}%, #C77700 100%, #2E7D32 100%)`;
                      }
                      return (
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${revenuePercent}%`,
                            background: gradient
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>
                
                {/* DB MTD */}
                <div>
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Direkte DB MTD</span>
                    <span className="text-lg font-bold text-white">
                      {monthlyTotalDB > 0 ? `${(monthlyTotalDB / 1000000).toFixed(2)}M kr` : '0 kr'}
                    </span>
                  </div>
                  <div className="relative h-4 bg-white/10 rounded-full overflow-hidden">
                    {/* Break-even marker */}
                    <div className="absolute top-0 bottom-0 w-0.5 bg-white/50 z-10" style={{ left: `${(monthlyDBBreakeven / monthlyDBTarget) * 100}%` }}></div>
                    {/* Progress bar with gradient zones */}
                    {(() => {
                      const dbPercent = Math.min((monthlyTotalDB / monthlyDBTarget) * 100, 100);
                      const breakevenPercent = (monthlyDBBreakeven / monthlyDBTarget) * 100;
                      let gradient;
                      if (dbPercent <= breakevenPercent) {
                        gradient = '#B3261E';
                      } else if (dbPercent < 100) {
                        gradient = `linear-gradient(to right, #B3261E 0%, #B3261E ${breakevenPercent}%, #C77700 ${breakevenPercent}%, #C77700 100%)`;
                      } else {
                        gradient = `linear-gradient(to right, #B3261E 0%, #B3261E ${breakevenPercent}%, #C77700 ${breakevenPercent}%, #2E7D32 100%)`;
                      }
                      return (
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${dbPercent}%`,
                            background: gradient
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              {/* Legend */}
              <div className="mt-3 flex justify-center gap-4 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B3261E]"></span> Under break-even</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#C77700]"></span> Break-even til budget</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#2E7D32]"></span> Over budget</span>
              </div>
            </SectionCard>

            <SectionCard title="Akkumuleret omsætning pr. dag">
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailyOmsaetningData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="dag" 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 8 }}
                      interval={4}
                    />
                    <YAxis 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
                      tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`}
                      width={35}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      formatter={(value, name) => [value ? `${(value/1000000).toFixed(2)}M kr` : 'N/A', name === 'omsaetning' ? 'Omsætning' : 'Break-even']}
                      labelFormatter={(label) => `Dag ${label}`}
                    />
                    <Bar 
                      dataKey="omsaetning" 
                      fill="#1E6F7A" 
                      name="Omsætning"
                      radius={[2, 2, 0, 0]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="breakeven" 
                      stroke="#B3261E" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Break-even"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="Akkumuleret dækningsbidrag pr. dag">
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailyDBData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="dag" 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 8 }}
                      interval={4}
                    />
                    <YAxis 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
                      tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`}
                      width={35}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      formatter={(value, name) => [value ? `${(value/1000000).toFixed(2)}M kr` : 'N/A', name === 'db' ? 'Direkte DB' : 'Break-even']}
                      labelFormatter={(label) => `Dag ${label}`}
                    />
                    <Bar 
                      dataKey="db" 
                      fill="#2E7D32" 
                      name="Direkte DB"
                      radius={[2, 2, 0, 0]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="breakeven" 
                      stroke="#B3261E" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Break-even"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </div>

          {/* ========== MIDDLE COLUMN: KØRENDE SALG ========== */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white pb-2 border-b border-white/10 flex items-center justify-between">
              <span>Kørende salg</span>
              <Link to="/pipeline" className="text-white/60 hover:text-white transition-colors">
                <ArrowRight className="w-5 h-5" />
              </Link>
            </h2>

            {/* Dækning Denne Uge - Kørende Salg */}
            <SectionCard title="Dækning denne uge">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white" data-testid="coverage-value">
                    {coverageLoading
                      ? 'Indlæser...'
                      : `${Math.round(coverageData?.summary?.total_db || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} kr`}
                  </p>
                </div>
              </div>
              <div className="mt-3">
                <ProgressBar 
                  value={coverageData?.summary?.total_db ? Number(coverageData.summary.total_db) : 0} 
                  max={775000} 
                  color="#1E6F7A" 
                  budgetLine={700000} 
                  budgetLabel="Mål" 
                />
              </div>
              {/* Estimeret dækning fra timer og bookinger */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-white/40 mb-2">Estimeret dækning</p>
                <div className="flex justify-between">
                  <span className="text-xs text-white/60">Fra timer</span>
                  <span className="text-xs font-medium text-white" data-testid="est-coverage-hours">
                    {Math.round((calendarCapacityData?.this_week?.timer || 0) * 2500).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} kr
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-white/60">Fra bookinger</span>
                  <span className="text-xs font-medium text-white" data-testid="est-coverage-bookings">
                    {Math.round((calendarCapacityData?.this_week?.bookinger || 0) * 4500).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} kr
                  </span>
                </div>
              </div>
            </SectionCard>

            {/* Nye bookinger akkumuleret */}
            <SectionCard title="Nye bookinger i denne uge">
              <div className="h-[108px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={nyeBookingerData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="dag" 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
                    />
                    <YAxis 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
                      domain={[0, 'auto']}
                      width={30}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      formatter={(value, name) => [value !== null ? value : 'N/A', name === 'akkumuleret' ? 'Resultat' : 'Mål']}
                    />
                    <Bar 
                      dataKey="akkumuleret" 
                      fill="#1E6F7A" 
                      name="akkumuleret"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="maal" 
                      stroke="#B3261E" 
                      strokeWidth={2}
                      dot={{ fill: '#B3261E', strokeWidth: 2, r: 3 }}
                      name="maal"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-white/60">
                  Pris pr. booking: <span className="text-white font-medium" data-testid="price-per-booking">
                    {newBookingsData && newBookingsData.price_per_booking 
                      ? `${Math.round(newBookingsData.price_per_booking).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} kr`
                      : 'Indlæser...'}
                  </span>
                </p>
                <p className="text-xs text-white/60">
                  I dag: <span className="text-white font-medium" data-testid="todays-bookings">
                    {(() => {
                      if (!newBookingsData?.daily_data) return 'Indlæser...';
                      const todayName = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'][new Date().getDay()];
                      const today = newBookingsData.daily_data.find(d => d.dag === todayName);
                      return today?.nye_bookinger ?? 0;
                    })()}
                  </span> bookinger
                </p>
              </div>
            </SectionCard>

            {/* KPT Speedometers */}
            <SectionCard title="Salgstimer og KPT">
              <div className="flex justify-around items-center">
                <HoursGauge 
                  value={calendarCapacityData?.this_week?.timer || 0} 
                  label="Timer denne uge"
                  size={77}
                />
                <Speedometer 
                  value={calendarCapacityData?.this_week?.kpt || 0} 
                  label="KPT denne uge"
                  size={77}
                />
                <HoursGauge 
                  value={calendarCapacityData?.next_week?.timer || 0} 
                  label="Timer næste uge"
                  size={77}
                />
                <Speedometer 
                  value={calendarCapacityData?.next_week?.kpt || 0} 
                  label="KPT næste uge"
                  size={77}
                />
              </div>
            </SectionCard>

            {/* Bookings */}
            <SectionCard title="Bookinger i kalenderen">
              <div className="space-y-4">
                {/* Denne uge */}
                <div>
                  <p className="text-xs text-white/60 mb-2">Denne uge</p>
                  <div className="relative">
                    {/* Labels above the bar */}
                    <div className="flex mb-1">
                      <span 
                        className="text-lg font-bold text-white text-center"
                        style={{ width: `${(bookingsThisWeek.bookinger / bookingsThisWeek.kapacitet) * 100}%` }}
                      >
                        {bookingsThisWeek.bookinger}
                      </span>
                      <span 
                        className="text-lg font-bold text-white text-center flex-1"
                      >
                        {bookingsThisWeek.ledigeTider}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(bookingsThisWeek.bookinger / bookingsThisWeek.kapacitet) * 100}%`,
                          backgroundColor: bookingsThisWeek.belaegning >= 85 ? '#2E7D32' : bookingsThisWeek.belaegning >= 70 ? '#C77700' : '#B3261E'
                        }}
                      />
                    </div>
                    {/* Belægningsprocent below the bar */}
                    <p className="text-sm text-white mt-1 text-center">Belægning: {bookingsThisWeek.belaegning}%</p>
                  </div>
                </div>
                {/* Næste uge */}
                <div>
                  <p className="text-xs text-white/60 mb-2">Næste uge</p>
                  <div className="relative">
                    {/* Labels above the bar */}
                    <div className="flex mb-1">
                      <span 
                        className="text-lg font-bold text-white text-center"
                        style={{ width: `${(bookingsNextWeek.bookinger / bookingsNextWeek.kapacitet) * 100}%` }}
                      >
                        {bookingsNextWeek.bookinger}
                      </span>
                      <span 
                        className="text-lg font-bold text-white text-center flex-1"
                      >
                        {bookingsNextWeek.ledigeTider}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(bookingsNextWeek.bookinger / bookingsNextWeek.kapacitet) * 100}%`,
                          backgroundColor: bookingsNextWeek.belaegning >= 85 ? '#2E7D32' : bookingsNextWeek.belaegning >= 70 ? '#C77700' : '#B3261E'
                        }}
                      />
                    </div>
                    {/* Belægningsprocent below the bar */}
                    <p className="text-sm text-white mt-1 text-center">Belægning: {bookingsNextWeek.belaegning}%</p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* ========== RIGHT COLUMN: ONLINE SALG ========== */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white pb-2 border-b border-white/10">
              Online salg
            </h2>

            <SectionCard title="Dækning denne uge">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white" data-testid="online-coverage-value">
                    {onlineCoverageData && onlineCoverageData.this_week_coverage 
                      ? `${Math.round(onlineCoverageData.this_week_coverage).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} kr`
                      : 'Indlæser...'}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>af 100.000 kr</p>
                </div>
              </div>
              <div className="mt-3">
                <ProgressBar 
                  value={onlineCoverageData?.this_week_coverage ? Number(onlineCoverageData.this_week_coverage) : 0} 
                  max={100000} 
                  color="#1E6F7A" 
                />
              </div>
            </SectionCard>

            <SectionCard title="Besøg og konvertering">
              <div className="h-[140px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={besogKonverteringData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="uge" 
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 9 }}
                      interval={1}
                    />
                    <YAxis 
                      yAxisId="left"
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: '#1E6F7A', fontSize: 10 }}
                      tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                      width={35}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      stroke="rgba(255,255,255,0.4)" 
                      tick={{ fill: '#C77700', fontSize: 10 }}
                      tickFormatter={(v) => `${v}%`}
                      width={35}
                      domain={[0, 'auto']}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      formatter={(value, name) => {
                        if (name === 'besog') return [value.toLocaleString('da-DK'), 'Besøg'];
                        return [`${value.toFixed(1)}%`, 'Konvertering'];
                      }}
                    />
                    <Bar 
                      yAxisId="left"
                      dataKey="besog" 
                      fill="#1E6F7A" 
                      name="besog"
                      radius={[4, 4, 0, 0]}
                      opacity={0.7}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="konvertering" 
                      stroke="#C77700" 
                      strokeWidth={2}
                      dot={{ fill: '#C77700', strokeWidth: 2, r: 3 }}
                      name="konvertering"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#1E6F7A]"></span> Besøg</span>
                <span className="flex items-center gap-1"><span className="w-3 h-1 bg-[#C77700]"></span> Konvertering %</span>
              </div>
            </SectionCard>

            <SectionCard title="Advarsler">
              <div className="h-[108px] overflow-y-auto">
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#C77700] flex-shrink-0 mt-0.5" />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>Data viser mangel på 25 montør timer om 8 uger.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#C77700] flex-shrink-0 mt-0.5" />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>Konverteringsprocenten i det kørende salg er faldet 2 uger i træk.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#B3261E] flex-shrink-0 mt-0.5" />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>KPT'en i næste uge ligger i øjeblikket kun på 0,43 for hele landet.</p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </div>

        {/* Debug Panel */}
        {showDebug && dashboardData?.debug_info && (
          <div className="mt-6 bg-[#1A1A1A] rounded-lg p-6 card-shadow fade-in" data-testid="debug-panel">
            <h3 className="text-sm font-semibold text-white mb-4">Admin debug view</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Row Counts</p>
                <pre className="text-xs text-white">{JSON.stringify(dashboardData.debug_info.row_counts, null, 2)}</pre>
              </div>
              <div>
                <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Cache Age</p>
                <p className="text-sm text-white">{dashboardData.debug_info.cache_age_seconds?.toFixed(0) ?? 'N/A'} seconds</p>
              </div>
              <div>
                <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Current User</p>
                <p className="text-sm text-white">{user?.email ?? 'Unknown'}</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-6">
          <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Luxsun Nordic · Dashboard · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};
